import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import { routes } from 'Routes'
import { Button } from '@material-ui/core'
import PropTypes from 'prop-types'
import { Edit3 as EditIcon } from 'react-feather'

import usePermission from 'hooks/usePermissions'
import useSnackbar from 'hooks/useSnackbar'
import useFetch from 'hooks/useFetch'

import {
  MenuButton,
  DialogLogs,
  ConfirmationRiskDialog,
  DialogHistoryNotifications,
} from 'components'
import {
  DialogConfirm,
  DialogReopen,
  InactivateDataProcessDialog,
  ReactivateDataProcessDialog,
  DeleteDataProcessDialog,
  DialogAcceptSuggested,
  DialogRefuseSuggested,
  DialogRevertLia,
} from '../../../'

import constants from 'constants/index'
import * as service from 'service'
import helpers from 'helpers'

const DataProcessMenuItems = ({
  dataProcess,
  origin,
  dataProcessHook,
  setLoading = () => {},
  onEvent = () => {},
  ...rest
}) => {
  const { statusesCheck } = helpers.dataProcess

  const history = useHistory()

  const { permitted } = usePermission()
  const snackbar = useSnackbar()

  // Dialogs States
  const [dialogConfirm, setDialogConfirm] = useState(false)
  const [dialogFragility, setDialogFragility] = useState(false)
  const [dialogReopen, setDialogReopen] = useState(false)
  const [dialogInactivateDataProcess, setDialogInactivateDataProcess] =
    useState(false)
  const [dialogReactivateDataProcess, setDialogReactivateDataProcess] =
    useState(false)
  const [dialogDeleteDataProcess, setDialogDeleteDataProcess] = useState(false)
  const [dialogLogs, setDialogLogs] = useState(false)
  const [dialogNotifications, setDialogNotifications] = useState(false)
  const [dialogAcceptSuggested, setDialogAcceptedSuggested] = useState(false)
  const [adoptedFragilityId, setAdoptedFragilityId] = useState(null)
  const [refuseDialog, setRefuseDialog] = useState({
    open: false,
    message: 'Deseja recusar a sugestão do processamento de dados atual?',
    type: 'refuse',
  })
  const [defaultFragilityId, setDefaultFragilityId] = useState(null)
  const [dialogRevertLia, setDialogRevertLia] = useState(false)

  const openRefuse = (props) => {
    setRefuseDialog({ ...refuseDialog, ...props })
  }

  const { response } = useFetch(service.dponet.departments.get, {
    perPage: 1000,
  })

  const handleEdit = () => {
    history.push(
      reverse(routes.dataProcess.edit, { dataProcessId: dataProcess?.id }),
    )
  }

  const isPendingLia = statusesCheck.isPendingLia(dataProcess)
  const isReviewLia =
    statusesCheck.isReviewLia(dataProcess) ||
    statusesCheck.isApprovedLia(dataProcess)

  const pendingOrDisapproved =
    statusesCheck.isPending(dataProcess) ||
    statusesCheck.disapproved(dataProcess)

  const pendingOrDisapprovedLia =
    isPendingLia || statusesCheck.isDisapprovedLia(dataProcess)

  const { response: responseLogs, isLoading: isLoadingLogs } = useFetch(
    service.dponet.dataProcesses.logs,
    {
      dataProcessId: dataProcess?.id,
    },
    [],
    dialogLogs,
  )

  const logs = isLoadingLogs ? [] : responseLogs?.data?.dponetAudits || []

  // Envia o processo para revisão
  const sendToReview = async () => {
    setLoading(true)
    setDialogConfirm(false)
    setDialogFragility(false)
    try {
      await service.dponet.dataProcesses.changeStatus({
        dataProcessId: dataProcess.id,
        statusId: constants.dataProcess.WAITING_REVIEW_STATUS,
        adoptedMatrixRiskId: adoptedFragilityId || defaultFragilityId,
      })
      snackbar.open({
        message: 'Processo enviado para a revisão!',
        variant: 'success',
      })
      setLoading(false)
      onEvent()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error) ||
          'Ocorreu um erro, tente novamente',
        variant: 'error',
      })
      setLoading(false)
    }
  }

  const getBackToList = () => {
    dataProcessHook.setReload(true)
  }

  const allowEdition = () => {
    return (
      permitted('create_data_process') &&
      (pendingOrDisapproved || pendingOrDisapprovedLia)
    )
  }

  const allowSendToRevision =
    permitted('create_data_process') && pendingOrDisapproved

  const allowReopen = () => {
    return (
      permitted('reopen_data_process') &&
      helpers.dataProcess.statusesCheck.approved(dataProcess)
    )
  }

  const allowInactivation = () => {
    return (
      permitted('inactive_and_reactive_data_process') &&
      helpers.dataProcess.statusesCheck.allowInactivation(dataProcess)
    )
  }

  const allowReactivate = () => {
    return (
      permitted('inactive_and_reactive_data_process') &&
      helpers.dataProcess.statusesCheck.inactive(dataProcess)
    )
  }

  const allowExclusion = () => {
    return permitted('destroy_data_process')
  }

  const allowAcceptedSuggestion = () => {
    return (
      permitted('list_and_manage_suggested_data_processes') &&
      (origin === 'DataProcessDiscarded' || origin === 'DataProcessSuggestion')
    )
  }

  const allowRefuseSuggestion = () => {
    return (
      permitted('list_and_manage_suggested_data_processes') &&
      origin === 'DataProcessSuggestion'
    )
  }

  const handleRevision = () => {
    if (dataProcess?.acceptProcessFragility) return setDialogFragility(true)

    return setDialogConfirm(true)
  }

  const items = [
    {
      label: 'EDITAR',
      action: () => handleEdit(),
      visible: allowEdition(),
    },
    {
      label: 'REABRIR',
      action: () => setDialogReopen(true),
      visible: allowReopen(),
    },
    {
      label: 'HISTÓRICO DE ALTERAÇÕES',
      action: () => setDialogLogs(true),
      visible: true,
    },
    {
      label: 'HISTÓRICO DE NOTIFICAÇÕES',
      action: () => setDialogNotifications(true),
      visible: true,
    },
    {
      label: 'INATIVAR',
      action: () => setDialogInactivateDataProcess(true),
      visible: allowInactivation(),
    },
    {
      label: 'REATIVAR',
      action: () => setDialogReactivateDataProcess(true),
      visible: allowReactivate(),
    },
    {
      label: 'EXCLUIR',
      action: () => setDialogDeleteDataProcess(true),
      visible: allowExclusion(),
    },
    {
      label: 'REVERTER LIA',
      action: () => setDialogRevertLia(true),
      visible: (isPendingLia || isReviewLia) && allowEdition(),
    },
  ]

  const SuggestedMenuItems = [
    {
      label: 'ACEITAR',
      action: () => setDialogAcceptedSuggested(true),
      visible: allowAcceptedSuggestion(),
    },
    {
      label: 'RECUSAR',
      action: () => openRefuse({ open: true }),
      visible: allowRefuseSuggestion(),
    },
  ]

  var menuItems = []

  if (origin === 'DataProcessSuggestion' || origin === 'DataProcessDiscarded') {
    menuItems = SuggestedMenuItems
  } else {
    menuItems = items
  }

  const visibleItems = menuItems.filter((item) => item.visible)

  return (
    <>
      {allowSendToRevision && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<EditIcon size={20} />}
          onClick={handleRevision}
          fullWidth
          id={constants.dataProcess.DATA_PROCESS_SHOW_DRIVER_STEP_2}
        >
          Enviar para revisão
        </Button>
      )}
      {visibleItems?.length > 0 && (
        <MenuButton
          id={constants.dataProcess.DATA_PROCESS_SHOW_DRIVER_STEP_1}
          {...rest}
        >
          {visibleItems.map((item) => (
            <Button key={item.label} fullWidth onClick={item.action}>
              {item.label}
            </Button>
          ))}
        </MenuButton>
      )}
      {dialogConfirm && (
        <DialogConfirm
          title={'Enviar processo para a revisão'}
          description={'Deseja enviar esse processo para revisão?'}
          confirmText={'Enviar para revisão'}
          open={dialogConfirm}
          setOpen={setDialogConfirm}
          handleConfirm={sendToReview}
        />
      )}
      {dialogFragility && (
        <ConfirmationRiskDialog
          setAdoptedFragilityId={setAdoptedFragilityId}
          dataProcessId={dataProcess?.id}
          handleConfirm={sendToReview}
          setOpen={setDialogFragility}
          open={dialogFragility}
          setDefaultFragilityId={setDefaultFragilityId}
        />
      )}
      <DialogReopen
        open={dialogReopen}
        setOpen={setDialogReopen}
        dataProcess={dataProcess}
        setLoading={setLoading}
        refresh={onEvent}
        redirectTo={reverse(routes.dataProcess.edit, {
          dataProcessId: dataProcess.id,
        })}
      />
      {dialogLogs && (
        <DialogLogs open={dialogLogs} setOpen={setDialogLogs} logs={logs} />
      )}
      <DialogHistoryNotifications
        open={dialogNotifications}
        setOpen={setDialogNotifications}
        notifications={dataProcess?.notificationMailerLogs}
      />
      <InactivateDataProcessDialog
        dataProcessId={dataProcess?.id}
        open={dialogInactivateDataProcess}
        setOpen={setDialogInactivateDataProcess}
        action={onEvent}
      />
      <ReactivateDataProcessDialog
        dataProcessId={dataProcess?.id}
        open={dialogReactivateDataProcess}
        setOpen={setDialogReactivateDataProcess}
        action={onEvent}
      />
      <DeleteDataProcessDialog
        dataProcessId={dataProcess?.id}
        open={dialogDeleteDataProcess}
        setOpen={setDialogDeleteDataProcess}
        action={getBackToList}
        redirectTo={routes.dataProcess.all}
      />
      <DialogAcceptSuggested
        open={dialogAcceptSuggested}
        setOpen={setDialogAcceptedSuggested}
        dataProcess={dataProcess}
        departments={response?.data?.departments || []}
        setIsLoading={setLoading}
      />
      <DialogRefuseSuggested
        refuseDialog={refuseDialog}
        setRefuseDialog={setRefuseDialog}
        dataProcess={dataProcess}
        setIsLoading={setLoading}
        redirectTo={routes.dataProcess.all}
      />
      <DialogRevertLia
        open={dialogRevertLia}
        setOpen={setDialogRevertLia}
        dataProcess={dataProcess}
        setLoading={setLoading}
        refresh={onEvent}
      />
    </>
  )
}

DataProcessMenuItems.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataProcess: PropTypes.object,
  origin: PropTypes.string,
  dataProcessHook: PropTypes.object,
  logs: PropTypes.array,
  setLoading: PropTypes.func,
  onEvent: PropTypes.func,
}

export default DataProcessMenuItems
